import { Component, computed, effect, EventEmitter, Inject, input, OnInit, Output, signal, } from '@angular/core';
import { Observable } from "rxjs";
import { LocationFinderComponent } from "../../location-finder/location-finder.component";
import { AddToCartDataInterface, GlobalStoreDeliveryMethod } from "../../models/cart";
import { StoreViewInterface } from "../../models/store";
import { HttpLocationService } from "../../services/http/location.service";
import { ModalService, PageService } from "@gea-waldviertler-website/shop-lib";
import { BuildupServiceComponent, SelectedBuildupServiceInterface } from '../buildup-service/buildup-service.component';
import { RemindAvailabilityComponent } from '../remind-availability/remind-availability.component';
import { OrderService } from "../../services/order.service";
import { FormBuilder, FormGroup } from "@angular/forms";
import { DistinctProductInterfaceType, GeaProduct } from "@gea-waldviertler-website/gea-models";
import { LocationService } from "../../services/location.service";
import { NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { NavigationEnd, Router } from "@angular/router";

export interface BuildupFormFieldsInterface {
  checked: boolean
}

@Component({
    selector: 'gea-shop-add-to-cart',
    templateUrl: './add-to-cart.component.html',
    styleUrls: ['./add-to-cart.component.scss'],
    standalone: false
})
export class AddToCartComponent implements OnInit {

  distinctProduct = input<DistinctProductInterfaceType | undefined>()
  product = input.required<GeaProduct>()

  @Output("triggerValidation") triggerValidationEvent = new EventEmitter<boolean>()
  @Output("addToCart") addToCartEvent = new EventEmitter<AddToCartDataInterface>()
  @Output("remindAvailability") remindAvailabilityEvent = new EventEmitter<DistinctProductInterfaceType>()

  mode = signal<GlobalStoreDeliveryMethod>("online")

  selectedStore = signal<StoreViewInterface | undefined>(undefined)

  buildupChecked = signal<boolean>(false)
  buildupService = signal<SelectedBuildupServiceInterface | undefined>(undefined)

  buildupServiceForm?: FormGroup

  locationFinderModal?: NgbModalRef
  buildupServiceModal?: NgbModalRef

  /**
   * Holds the quantity set by the user input, by default 0
   */
  selectedQuantity = signal<number>(1)

  /**
   * If a distinctProduct is set, an api call will be triggered and pushed onto this signal
   */
  locations = signal<StoreViewInterface[]>([])

  /**
   * On setting this.locations signal, this holds the count of stores having
   * the distinctProduct (incl. selectedQuantity) on stock
   */
  inStoresAvailable = signal<number>(0)

  inStoresAvailableString = computed(() => {
    const count = this.inStoresAvailable()

    switch (count) {
      case 0: return 'keiner Filiale'
      case 1: return 'einer Filiale'
      case 2: return 'zwei Filialen'
      case 3: return 'drei Filialen'
      case 11: return 'elf Filialen'
      case 12: return 'zwölf Filialen'
    }

    return count + ' Filialen'
  })

  /**
   * This reflects the bootstrap.config.storePickupEnabled variable and allows to enable/disable
   * the store pickup functionality globally
   */
  storePickupEnabled = computed<boolean>(() => {
    let bootstrapData = this.pageService.getBootstrapSignal()()
    if (bootstrapData) {
      return bootstrapData.config.storePickupEnabled
    }
    return false
  })

  /**
   * If a distinctProduct is set, this returns true if this product allows a reminder (online)
   */
  isRemindPossible = computed<boolean | undefined>(() =>  {
    const dp = this.distinctProduct()
    if (! dp) return undefined

    const variantId = dp.selectedVariant
    const variant = dp.product.getVariant(variantId)

    if(variant.deliveryCategory.code == 'G') {
      // TODO: BAs berücksichtigen
      return false
    }

    return true
  })

  isPreorderPossible = computed<boolean>(() => {
    const dp = this.distinctProduct()
    const p = this.product()

    // when online on stock, the product will be sent to the store, so store pickup is possible
    const s = p.getStock(dp?.selectedVariant ?? 0, dp?.distinctId)
    if (s && s > 0) return true

    if (! dp) return p.isPreorderPossible()
    return dp.product.isPreorderPossible(dp.selectedVariant)
  })

  isStorePickupForbiddenByVariant = computed(() => {
    const dp = this.distinctProduct()
    if (! dp) {
      return ! this.product().isStorePickupAllowed()
    }
    return ! this.product().isStorePickupAllowed(dp.selectedVariant)
  })

  isStorePickupDisabledInView = computed(() => {
    const selStore = this.selectedStore()
    return this.isStorePickupForbiddenByVariant()
      || ! this.storePickupEnabled()
      || (this.inStoresAvailable() == 0 && ! this.isPreorderPossible())
      || (selStore && !selStore.storePickupPossible)
  })

  storePickupSpeed = computed(() => {
    const dp = this.distinctProduct()
    if (! dp) return ''

    const dt = dp.product.getDeliveryTime(
      dp.selectedVariant,
      dp.distinctId,
      dp.qualityId
    )
    if (! dt) return ''

    if (dt.daysFrom < 8) {
      return "quick"
    } else {
      return "medium"
    }
  })

  storePickupMessage = computed(() => {
    const dp = this.distinctProduct()
    if (! dp) return ''

    let message = dp.product.getDeliveryTime(
      dp.selectedVariant,
      dp.distinctId,
      dp.qualityId
    )?.text

    if (! message) {
      return '(bald verfügbar)'
    }

    if (message.endsWith('Werktage')) {
      return '(in ' + message + 'n verfügbar)'
    }

    return '(in ' + message + ' verfügbar)'
  })

  showStorePickupTile = computed(() => {
    const dp = this.distinctProduct()
    if (! dp) return false
    const variant = dp.product.getVariant(dp.selectedVariant)
    if (! variant) return false
    return ! (variant.freeDelivery && !variant.storePickUp)
  })

  constructor(
    @Inject(ModalService) private modalService: ModalService,
    @Inject(HttpLocationService) private locationService: HttpLocationService,
    @Inject(OrderService) private orderService: OrderService,
    @Inject(PageService) private pageService: PageService,
    private fb: FormBuilder,
    private router: Router,
  ) {
    effect(() => {
      const dp = this.distinctProduct()
      if (! dp) return

      this.getLocations(dp).subscribe(locations => {
        LocationService.sortLocationsByStorePickupPriority(locations)
        this.locations.set(locations)
        let isa = 0
        for (let location of locations) {
          if (location.stock >= this.selectedQuantity()) {
            isa++
          }
        }
        this.inStoresAvailable.set(isa)
        if (! isa) {
          this.toggleMode('online')
        }
      })

      if (dp.product?.typeId == 2) {
        const order = this.orderService.order()
        this.buildupService.set(order.buildupService)
        if (order.buildupService) {
          this.buildupServiceForm?.controls['checked'].setValue(true)
        }
      }
    })

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.locationFinderModal?.close()
        this.buildupServiceModal?.close()
      }
    })
  }

  ngOnInit(): void {
    this.buildupServiceForm = this.fb.group({
      checked: [this.buildupChecked()],
    })
    this.buildupServiceForm.valueChanges.subscribe((formData: BuildupFormFieldsInterface) => {
      this.buildupChecked.set(formData.checked)
      if (! formData.checked) {
        this.buildupService.set(undefined)
        this.toggleMode('online')
      } else {
        this.toggleMode('buildup')
        if (! this.buildupService()) this.openBuildupServiceModal()
      }
    })
  }

  onSelectedQuantityChange(event: Event) {
    const value = (event.target as HTMLSelectElement).value
    this.selectedQuantity.set(parseInt(value))
  }

  openLocationFinderModal() {
    if (this.isStorePickupForbiddenByVariant()
      || ! this.storePickupEnabled()
      || (this.inStoresAvailable() == 0 && ! this.isPreorderPossible())) return

    let that = this
    this.locationFinderModal = this.modalService.show(LocationFinderComponent, {
      title: "Verfügbarkeit prüfen",
      inputs: {
        distinctProduct: that.distinctProduct(),
        locations: that.locations(),
        quantity: that.selectedQuantity(),
        selectedLocation: that.selectedStore()
      },
      closeBtnText: undefined,
      modalOptions: {
        windowClass: 'location-finder-modal',
      },
      subscribers: {
        selectLocationEvent: function(store: StoreViewInterface) {
          if (that.locationFinderModal) that.locationFinderModal.close()
          that.onSelectStore(store)
        }
      }
    })
    if (this.locationFinderModal) {
      this.locationFinderModal.dismissed.subscribe(() => {
        if (! this.selectedStore()) {
          this.toggleMode('online')
        }
      })
    }
  }

  openBuildupServiceModal() {
    let that = this
    this.buildupServiceModal = this.modalService.show(BuildupServiceComponent, {
      title: "Aufbauservice Verfügbarkeit",
      closeBtnText: undefined,
      modalOptions: {
        windowClass: 'buildup-service-modal',
        size: "lg"
      },
      subscribers: {
        checkBuildUpEvent: function(buildupService: SelectedBuildupServiceInterface | undefined) {
          if (buildupService?.assembly == null) {
            that.buildupServiceForm?.controls['checked'].setValue(false)
          } else {
            that.buildupChecked.set(true)
            that.buildupService.set(buildupService)
          }

          that.buildupServiceModal?.close()
        }
      }
    })
  }
  onRecheckAssemblySelection(e: MouseEvent) {
    e.stopPropagation()
    this.buildupChecked.set(false)
    this.buildupService.set(undefined)
    this.openBuildupServiceModal()
  }
  
  addToCart(distinctProduct: DistinctProductInterfaceType | undefined) {
    if (! distinctProduct) return
    // do not allow addToCart if disabled
    if (this.mode() == 'store' && ! this.storePickupEnabled()) {
      return
    }
    
    let v: AddToCartDataInterface = {
      distinctProduct: distinctProduct,
      quantity: this.selectedQuantity(),
      buildupService: false
    }

    const buildupService = this.buildupService()
    if (this.mode() == 'store' && this.storePickupEnabled() && this.selectedStore()) {
      v.store = this.selectedStore()
    } else if (buildupService && buildupService.assembly && this.buildupChecked()) {
      v.buildupService = true
      this.orderService.setBuildupService(buildupService)
    }

    this.addToCartEvent.emit(v)
  }

  openRemindAvailability() {
    if (! this.distinctProduct()) return
    if (this.isInStock() || !this.isRemindPossible()) return

    let that = this
    let modal = this.modalService.show(RemindAvailabilityComponent, {
      title: "Bei Verfügbarkeit benachrichtigen",
      inputs: {
        distinctProduct: that.distinctProduct
      },
      closeBtnText: undefined,
      modalOptions: {
        windowClass: 'remind-availability-modal',
      },
      subscribers: {
        submitRemindRequest: function() {
          modal.close()
        }
      }
    })
    
    this.remindAvailabilityEvent.emit(this.distinctProduct())
  }

  onSelectStore(store: StoreViewInterface | undefined) {
    this.selectedStore.set(store)
  }

  toggleMode(mode: GlobalStoreDeliveryMethod) {
    // click when disabled
    if (! this.distinctProduct()) return
    if (mode == "store" && this.isStorePickupDisabledInView()) return

    // click when store pickup is not possible to any reason
    if (mode == "store" &&
      (this.isStorePickupForbiddenByVariant()
      || (this.inStoresAvailable() === 0) && ! this.isPreorderPossible())) {
      return
    }

    if (mode == "online") {
      this.selectedStore.set(undefined)
    } else {
      if (! this.selectedStore()) this.openLocationFinderModal()
    }

    this.mode.set(mode)
  }

  /**
   * returns boolean if on stock or not or undefined if not sure (stock request
   * hasn't been applied or distinctProduct hasn't been loaded already)
   */
  isInStock(): boolean | undefined {
    const dp = this.distinctProduct()
    if (! dp) return undefined
    return dp.product.canBeOrdered(false, this.selectedQuantity(), dp.selectedVariant, dp.distinctId)
  }

  getLocations(distinctProduct?: DistinctProductInterfaceType): Observable<StoreViewInterface[]> {
    return this.locationService.getLocationsForDistinctProduct(distinctProduct, this.selectedQuantity())
  }
}
